import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../api/axiosInstance';
import {
  PresentationChartBarIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  PlayIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  VideoCameraIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

/**
 * Live view of the rehearsal/training browser (2026-08-31). Polls the
 * latest screencast frame + run status. In 'train' mode the owner DRIVES
 * the browser — clicks on the image, typing, navigation — and every action
 * is captured as a demo step; in 'watch' mode (rehearsal) it is read-only,
 * showing each step's verdict as it happens so failures (like a login
 * wall) are seen, not inferred.
 */
const LiveBrowserModal = ({ mode, title, onClose, onStop, showNotification }) => {
  const [frame, setFrame] = useState(null);
  const [status, setStatus] = useState(null);
  const [urlBar, setUrlBar] = useState('');
  const [typeText, setTypeText] = useState('');
  const [stopping, setStopping] = useState(false);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const s = await axiosInstance.get('/api/ai/demo-scripts/live/status');
        if (!alive) return;
        setStatus(s.data?.data || null);
        const f = await axiosInstance.get('/api/ai/demo-scripts/live/frame').catch(() => null);
        if (alive && f?.data?.data?.frame) setFrame(f.data.data.frame);
      } catch (_) { /* transient — keep polling */ }
    };
    tick();
    const iv = setInterval(tick, 900);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const sendInput = async (payload) => {
    try {
      await axiosInstance.post('/api/ai/demo-scripts/live/input', payload);
    } catch (err) {
      showNotification?.(err.response?.data?.error || 'Browser input failed', 'error');
    }
  };
  const clickFrame = (e) => {
    if (mode !== 'train') return;
    const rect = e.currentTarget.getBoundingClientRect();
    sendInput({
      type: 'click',
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  };
  const doStop = async (discard) => {
    setStopping(true);
    try { await onStop(discard); } finally { setStopping(false); }
  };

  const failed = status?.error || /FAILED/i.test(status?.phase || '');

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#161616] rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col border border-gray-200 dark:border-gray-800 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${status?.running ? 'bg-green-500 animate-pulse' : status?.done ? (status?.pass === false || failed ? 'bg-red-500' : 'bg-gray-400') : 'bg-yellow-500 animate-pulse'}`} />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{title}</h2>
            {mode === 'train' && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400">
                recording — {status?.running ? 'your clicks become demo steps' : 'starting…'}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-[#222222]">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex">
          {/* Browser frame */}
          <div className="flex-1 bg-gray-950 flex items-center justify-center overflow-auto p-2">
            {frame ? (
              /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */
              <img
                src={`data:image/jpeg;base64,${frame}`}
                alt="Live demo browser"
                onClick={clickFrame}
                className={`max-w-full max-h-full rounded ${mode === 'train' ? 'cursor-crosshair' : ''}`}
                draggable={false}
              />
            ) : (
              <div className="text-center text-gray-400 text-xs py-24">
                <ArrowPathIcon className="w-6 h-6 mx-auto mb-2 animate-spin" />
                {status?.done ? 'Session ended before a frame arrived' : 'Waiting for the browser…'}
              </div>
            )}
          </div>

          {/* Progress panel (watch mode) */}
          {mode === 'watch' && (
            <div className="w-64 border-l border-gray-100 dark:border-gray-800 p-3 overflow-y-auto flex-shrink-0">
              <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-2">Progress</p>
              <div className="space-y-1.5">
                {(status?.beats || []).map((b) => (
                  <div key={b.id} className="flex items-start gap-1.5 text-[11px] p-1.5 rounded-lg bg-gray-50 dark:bg-[#1a1a1a]">
                    {b.ok
                      ? <CheckCircleIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                      : <ExclamationTriangleIcon className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />}
                    <div className="min-w-0">
                      <span className="text-gray-800 dark:text-gray-200">{b.title || b.id}</span>
                      {!b.ok && (b.actions || []).filter(a => !a.ok).map((a, i) => (
                        <p key={i} className="text-red-600 dark:text-red-400 break-words">{a.action}: {a.error}</p>
                      ))}
                    </div>
                  </div>
                ))}
                {(status?.beats || []).length === 0 && !status?.done && (
                  <p className="text-[11px] text-gray-400">No steps run yet…</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Status line */}
        <div className={`px-5 py-2 text-[11px] border-t border-gray-100 dark:border-gray-800 ${failed ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
          {status?.error ? `Error: ${status.error}` : (status?.phase || 'connecting…')}
          {status?.done && status?.pass != null && (
            <span className={`ml-2 font-medium ${status.pass ? 'text-green-600' : 'text-red-600'}`}>
              {status.pass ? '· rehearsal PASSED' : '· rehearsal FAILED'}
            </span>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 px-5 py-3 border-t border-gray-100 dark:border-gray-800">
          {mode === 'train' ? (
            <>
              <form className="flex items-center gap-1.5 flex-1 min-w-0" onSubmit={(e) => { e.preventDefault(); if (urlBar.trim()) sendInput({ type: 'navigate', url: /^https?:\/\//i.test(urlBar.trim()) ? urlBar.trim() : `https://${urlBar.trim()}` }); }}>
                <input
                  className="flex-1 min-w-0 px-2.5 py-1.5 text-xs rounded-lg bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none"
                  value={urlBar} onChange={(e) => setUrlBar(e.target.value)} placeholder="Go to URL…"
                />
                <button type="submit" className="p-1.5 rounded-lg bg-gray-100 dark:bg-[#222222] text-gray-600 dark:text-gray-300 hover:bg-gray-200"><ArrowRightIcon className="w-3.5 h-3.5" /></button>
              </form>
              <form className="flex items-center gap-1.5 flex-1 min-w-0" onSubmit={(e) => { e.preventDefault(); if (typeText) { sendInput({ type: 'type', text: typeText }); setTypeText(''); } }}>
                <input
                  className="flex-1 min-w-0 px-2.5 py-1.5 text-xs rounded-lg bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none"
                  value={typeText} onChange={(e) => setTypeText(e.target.value)} placeholder="Type into the focused field…"
                />
                <button type="submit" className="px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-[#222222] text-[11px] text-gray-600 dark:text-gray-300 hover:bg-gray-200">Type</button>
              </form>
              <button onClick={() => sendInput({ type: 'key', key: 'Enter' })} className="px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-[#222222] text-[11px] text-gray-600 dark:text-gray-300 hover:bg-gray-200">Enter ⏎</button>
              <button onClick={() => sendInput({ type: 'scroll', dy: -400 })} className="p-1.5 rounded-lg bg-gray-100 dark:bg-[#222222] text-gray-600 dark:text-gray-300 hover:bg-gray-200"><ChevronUpIcon className="w-3.5 h-3.5" /></button>
              <button onClick={() => sendInput({ type: 'scroll', dy: 400 })} className="p-1.5 rounded-lg bg-gray-100 dark:bg-[#222222] text-gray-600 dark:text-gray-300 hover:bg-gray-200"><ChevronDownIcon className="w-3.5 h-3.5" /></button>
              <div className="flex-shrink-0 flex items-center gap-2 ml-2">
                <button onClick={() => doStop(true)} disabled={stopping}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-[#222222] disabled:opacity-50">
                  Discard
                </button>
                <button onClick={() => doStop(false)} disabled={stopping}
                  className="px-3 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-50">
                  {stopping ? 'Building steps…' : 'Stop & build steps'}
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-between">
              <p className="text-[11px] text-gray-400">Read-only — the rehearsal replays the script exactly as saved.</p>
              <button onClick={onClose} className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-[#222222]">
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Deterministic browser actions a demo beat can perform. Each action type
// shows only the fields it needs; validation mirrors the backend.
const ACTION_TYPES = [
  { value: 'navigate', label: 'Open URL', fields: ['url'] },
  { value: 'click', label: 'Click element', fields: ['selector'] },
  { value: 'type', label: 'Type into field', fields: ['selector', 'text'] },
  { value: 'press', label: 'Press key', fields: ['key'] },
  { value: 'scroll', label: 'Scroll (px)', fields: ['y'] },
  { value: 'wait', label: 'Wait (ms)', fields: ['ms'] },
  { value: 'waitFor', label: 'Wait for element', fields: ['selector'] },
];

const emptyBeat = () => ({ id: '', title: '', say: '', actions: [] });
const emptyLogin = () => ({ username: '', password: '', hasPassword: false, actions: [] });
const emptyArea = () => ({ title: '', url: '', description: '' });
const emptyForm = () => ({
  productKey: '', productName: '', description: '', startUrl: '',
  enabled: true, beats: [emptyBeat()], login: emptyLogin(), showLogin: false,
  productMap: [], productNotes: '',
});

const STATUS_CHIP = {
  ready: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400',
  failing: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400',
  draft: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
};

const ProductDemos = ({ showNotification }) => {
  const [loading, setLoading] = useState(true);
  const [demos, setDemos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // demo being edited (null = create)
  const [rehearsingId, setRehearsingId] = useState(null);
  const [expandedReport, setExpandedReport] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  // {mode:'watch'|'train', title, demo} — live browser-session modal
  const [liveView, setLiveView] = useState(null);

  const fetchDemos = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/ai/demo-scripts');
      setDemos(res.data?.data || []);
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Failed to load product demos', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { fetchDemos(); }, [fetchDemos]);

  const openCreate = () => { setEditing(null); setFormData(emptyForm()); setShowModal(true); };
  const openEdit = (demo) => {
    setEditing(demo);
    const hasLogin = !!(demo.login?.username || demo.login?.hasPassword || demo.login?.actions?.length);
    setFormData({
      productKey: demo.productKey,
      productName: demo.productName,
      description: demo.description || '',
      startUrl: demo.startUrl || '',
      enabled: demo.enabled,
      beats: demo.beats?.length ? demo.beats.map(b => ({ ...b, actions: b.actions || [] })) : [emptyBeat()],
      login: {
        username: demo.login?.username || '',
        password: '', // write-only: never returned by the API
        hasPassword: !!demo.login?.hasPassword,
        actions: demo.login?.actions || [],
      },
      showLogin: hasLogin,
      productMap: demo.productMap || [],
      productNotes: demo.productNotes || '',
    });
    setShowModal(true);
  };

  // Owners type "acme.com" and "Step 1" — normalize instead of rejecting:
  // URLs get https:// when the scheme is missing; step ids become slugs.
  const normalizeUrl = (u) => {
    const s = (u || '').trim();
    if (!s) return '';
    return /^https?:\/\//i.test(s) ? s : `https://${s}`;
  };
  const slugifyId = (id, fallback) => {
    const s = (id || '').trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');
    return s || fallback;
  };

  const cleanActions = (actions) => actions.map((a) => {
    const fields = ACTION_TYPES.find(t => t.value === a.type)?.fields || [];
    const out = { type: a.type };
    for (const f of fields) {
      if (a[f] !== '' && a[f] !== undefined && a[f] !== null) {
        out[f] = (f === 'y' || f === 'ms') ? Number(a[f]) : (f === 'url' ? normalizeUrl(a[f]) : a[f]);
      }
    }
    return out;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        productName: formData.productName.trim(),
        description: formData.description.trim(),
        startUrl: normalizeUrl(formData.startUrl),
        enabled: formData.enabled,
        beats: formData.beats.map((b, i) => ({
          // The id is internal — derived from the existing id (edits) or the
          // title, never typed by the owner.
          id: slugifyId(b.id || b.title, `step-${i + 1}`),
          title: (b.title || '').trim(),
          say: b.say.trim(),
          actions: cleanActions(b.actions),
        })),
        productMap: formData.productMap
          .filter((a) => (a.title || '').trim() && (a.url || '').trim())
          .map((a) => ({
            title: a.title.trim(),
            url: normalizeUrl(a.url),
            description: (a.description || '').trim(),
          })),
        productNotes: formData.productNotes.trim(),
      };
      if (formData.showLogin) {
        payload.login = {
          username: formData.login.username.trim(),
          // Password semantics: value = replace; empty + never-set = noop
          // (omit); empty + previously-set = keep existing (omit).
          ...(formData.login.password ? { password: formData.login.password } : {}),
          actions: cleanActions(formData.login.actions),
        };
      } else if (editing) {
        payload.login = null; // login section removed — clear stored secrets
      }
      if (editing) {
        await axiosInstance.put(`/api/ai/demo-scripts/${editing.id}`, payload);
        showNotification?.('Demo updated — rehearse it to mark it Ready', 'success');
      } else {
        await axiosInstance.post('/api/ai/demo-scripts', { ...payload, productKey: formData.productKey.trim().toLowerCase() });
        showNotification?.('Demo created — rehearse it to mark it Ready', 'success');
      }
      setShowModal(false);
      fetchDemos();
    } catch (err) {
      const details = err.response?.data?.details;
      showNotification?.(
        (Array.isArray(details) && details[0]?.message) || err.response?.data?.message || 'Save failed',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (demo) => {
    if (!window.confirm(`Delete the "${demo.productName}" demo? Agents will no longer be able to demo it.`)) return;
    try {
      await axiosInstance.delete(`/api/ai/demo-scripts/${demo.id}`);
      showNotification?.('Demo deleted', 'success');
      fetchDemos();
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Delete failed', 'error');
    }
  };

  const handleToggle = async (demo) => {
    try {
      await axiosInstance.put(`/api/ai/demo-scripts/${demo.id}`, { enabled: !demo.enabled });
      fetchDemos();
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Update failed', 'error');
    }
  };

  const handleRehearse = async (demo) => {
    setRehearsingId(demo.id);
    // Open the live view alongside — the rehearsal browser streams here,
    // so a failure is SEEN (e.g. stuck on a login wall), not inferred.
    setLiveView({ mode: 'watch', title: `Rehearsing — ${demo.productName}`, demo });
    try {
      const res = await axiosInstance.post(`/api/ai/demo-scripts/${demo.id}/rehearse`);
      const updated = res.data?.data;
      showNotification?.(
        updated?.status === 'ready'
          ? 'Rehearsal passed — demo is Ready'
          : 'Rehearsal FAILED — see the step report',
        updated?.status === 'ready' ? 'success' : 'error'
      );
      if (updated?.status === 'failing') setExpandedReport(demo.id);
      fetchDemos();
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Rehearsal could not run', 'error');
    } finally {
      setRehearsingId(null);
    }
  };

  // --- training (walkthrough recorder) ------------------------------------
  // The owner drives the real product in a live browser; every click, typed
  // field, and page change is captured as a demo step. On stop, the captured
  // steps open in the editor for talking-point review — training the agent
  // by example instead of hand-writing selectors.
  const startTraining = async (demo) => {
    const suggested = demo?.startUrl || '';
    const url = window.prompt(
      'Start URL for the training session (the page the browser opens first):',
      suggested || 'https://'
    );
    if (!url || url === 'https://') return;
    try {
      await axiosInstance.post('/api/ai/demo-scripts/recorder/start', {
        url: /^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`,
        productName: demo?.productName || null,
      });
      setLiveView({ mode: 'train', title: demo ? `Training — ${demo.productName}` : 'Record new demo walkthrough', demo: demo || null });
    } catch (err) {
      showNotification?.(err.response?.data?.error || err.response?.data?.message || 'Could not start the training browser', 'error');
    }
  };

  const stopTraining = async (discard) => {
    const demo = liveView?.demo || null;
    try {
      const res = await axiosInstance.post('/api/ai/demo-scripts/recorder/stop', {
        productName: demo?.productName || null,
        ...(discard ? { discard: true } : {}),
      });
      setLiveView(null);
      if (discard) return;
      const script = res.data?.data?.script;
      const captured = (script?.beats || []).map((b, i) => ({
        id: b.id || `step-${i + 1}`,
        title: b.title || '',
        say: b.say || '',
        actions: b.actions || [],
      }));
      if (!captured.length) {
        showNotification?.('No steps were captured — click around the product while recording', 'error');
        return;
      }
      if (demo) {
        openEdit(demo);
        setFormData(fd => ({ ...fd, beats: captured }));
      } else {
        setEditing(null);
        setFormData({
          ...emptyForm(),
          productName: script?.productName || '',
          startUrl: script?.startUrl || '',
          beats: captured,
        });
        setShowModal(true);
      }
      showNotification?.(`Captured ${captured.length} step(s) — review the talking points, then save and rehearse`, 'success');
    } catch (err) {
      setLiveView(null);
      showNotification?.(err.response?.data?.error || err.response?.data?.message || 'Could not build steps from the recording', 'error');
    }
  };

  // --- form helpers -------------------------------------------------------
  const setBeat = (i, patch) => setFormData(fd => ({
    ...fd, beats: fd.beats.map((b, j) => (j === i ? { ...b, ...patch } : b)),
  }));
  const moveBeat = (i, dir) => setFormData(fd => {
    const beats = [...fd.beats];
    const j = i + dir;
    if (j < 0 || j >= beats.length) return fd;
    [beats[i], beats[j]] = [beats[j], beats[i]];
    return { ...fd, beats };
  });
  const removeBeat = (i) => setFormData(fd => ({ ...fd, beats: fd.beats.filter((_, j) => j !== i) }));
  const addBeat = () => setFormData(fd => ({ ...fd, beats: [...fd.beats, emptyBeat()] }));
  const setAction = (bi, ai, patch) => setBeat(bi, {
    actions: formData.beats[bi].actions.map((a, j) => (j === ai ? { ...a, ...patch } : a)),
  });
  const addAction = (bi) => setBeat(bi, { actions: [...formData.beats[bi].actions, { type: 'navigate' }] });
  const removeAction = (bi, ai) => setBeat(bi, { actions: formData.beats[bi].actions.filter((_, j) => j !== ai) });
  const setLogin = (patch) => setFormData(fd => ({ ...fd, login: { ...fd.login, ...patch } }));
  const setLoginAction = (ai, patch) => setLogin({
    actions: formData.login.actions.map((a, j) => (j === ai ? { ...a, ...patch } : a)),
  });
  const addLoginAction = () => setLogin({ actions: [...formData.login.actions, { type: 'type', selector: '', text: '' }] });
  const removeLoginAction = (ai) => setLogin({ actions: formData.login.actions.filter((_, j) => j !== ai) });

  const canSave = formData.productName.trim()
    && (editing || /^[a-z0-9][a-z0-9-]{0,40}$/.test(formData.productKey.trim().toLowerCase()))
    && formData.beats.length > 0
    && formData.beats.every(b => b.say.trim().length >= 3);

  const inputCls = 'w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-400';

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-yellow-50 dark:bg-yellow-900/20">
            <PresentationChartBarIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-500" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Product Demos</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Live demos your AI agents can give in meetings — the agent drives a real browser, shares it as its screen, and narrates each step.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => startTraining(null)}
            title="Open a live browser, drive the product yourself, and your clicks become demo steps"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-[#222222] text-gray-700 dark:text-gray-300 text-xs font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-[#2a2a2a] transition-colors"
          >
            <VideoCameraIcon className="w-4 h-4" /> Record walkthrough
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
          >
            <PlusIcon className="w-4 h-4" /> New demo
          </button>
        </div>
      </div>

      <div className="mb-4 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-900/30 text-xs text-yellow-800 dark:text-yellow-300">
        Point demos at demo accounts only — never at an environment with real customer data. Rehearse after every edit: a passing rehearsal marks the demo Ready; agents only present what actually works.
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2].map(i => <div key={i} className="h-24 bg-gray-100 dark:bg-[#1a1a1a] rounded-xl animate-pulse" />)}
        </div>
      ) : demos.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl bg-yellow-50 dark:bg-yellow-900/20 flex items-center justify-center mx-auto mb-4">
            <PresentationChartBarIcon className="w-7 h-7 text-yellow-600 dark:text-yellow-500" />
          </div>
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">No product demos yet</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Create one and your agents can demo it live in any meeting.</p>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
          >
            <PlusIcon className="w-4 h-4" /> New demo
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {demos.map((demo) => (
            <div key={demo.id} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#161616] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{demo.productName}</span>
                    <span className="text-[10px] font-mono text-gray-400">{demo.productKey}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${STATUS_CHIP[demo.status] || STATUS_CHIP.draft}`}>
                      {demo.status}
                    </span>
                    {!demo.enabled && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-500">disabled</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {demo.beats?.length || 0} steps
                    {demo.lastRehearsal?.at && (
                      <> · rehearsed {new Date(demo.lastRehearsal.at).toLocaleString()} ({demo.lastRehearsal.pass ? 'passed' : 'failed'}, {Math.round((demo.lastRehearsal.durationMs || 0) / 1000)}s)</>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => handleRehearse(demo)}
                    disabled={rehearsingId === demo.id}
                    title="Rehearse: replay every step in a real browser"
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-[#222222] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#2a2a2a] transition-colors disabled:opacity-50"
                  >
                    {rehearsingId === demo.id
                      ? <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      : <PlayIcon className="w-4 h-4" />}
                    Rehearse
                  </button>
                  <button
                    onClick={() => startTraining(demo)}
                    title="Train: drive the product in a live browser — your clicks replace this demo's steps"
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-[#222222] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#2a2a2a] transition-colors"
                  >
                    <VideoCameraIcon className="w-4 h-4" /> Train
                  </button>
                  <button
                    onClick={() => handleToggle(demo)}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${demo.enabled
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100'
                      : 'bg-gray-100 dark:bg-[#222222] text-gray-500 hover:bg-gray-200'}`}
                  >
                    {demo.enabled ? 'Enabled' : 'Enable'}
                  </button>
                  <button onClick={() => openEdit(demo)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-[#222222] transition-colors">
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(demo)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Rehearsal report */}
              {demo.lastRehearsal?.beats?.length > 0 && (
                <div className="mt-3">
                  <button
                    onClick={() => setExpandedReport(expandedReport === demo.id ? null : demo.id)}
                    className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  >
                    {expandedReport === demo.id ? 'Hide' : 'Show'} step report
                  </button>
                  {expandedReport === demo.id && (
                    <div className="mt-2 space-y-1">
                      {demo.lastRehearsal.beats.map((b) => (
                        <div key={b.id} className="flex items-start gap-2 text-xs p-2 rounded-lg bg-gray-50 dark:bg-[#1a1a1a]">
                          {b.ok
                            ? <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                            : <ExclamationTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />}
                          <div className="min-w-0">
                            <span className="text-gray-800 dark:text-gray-200">{b.title || b.id}</span>
                            {!b.ok && (b.actions || []).filter(a => !a.ok).map((a, i) => (
                              <p key={i} className="text-red-600 dark:text-red-400 truncate">{a.action}: {a.error}</p>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Live browser view — watch a rehearsal or drive a training session */}
      {liveView && (
        <LiveBrowserModal
          mode={liveView.mode}
          title={liveView.title}
          showNotification={showNotification}
          onClose={() => setLiveView(null)}
          onStop={stopTraining}
        />
      )}

      {/* Editor modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#161616] rounded-2xl w-full max-w-3xl max-h-[88vh] flex flex-col border border-gray-200 dark:border-gray-800 shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                {editing ? `Edit demo — ${editing.productName}` : 'New product demo'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-[#222222]">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-4 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Product name</label>
                  <input className={inputCls} value={formData.productName}
                    onChange={e => setFormData(fd => ({ ...fd, productName: e.target.value }))} placeholder="Acme CRM" />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Key {editing && <span className="text-gray-400">(fixed)</span>}
                  </label>
                  <input className={`${inputCls} font-mono disabled:opacity-50`} value={formData.productKey} disabled={!!editing}
                    onChange={e => setFormData(fd => ({ ...fd, productKey: e.target.value }))} placeholder="acme-crm" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Start URL (demo environment)</label>
                <input className={inputCls} value={formData.startUrl}
                  onChange={e => setFormData(fd => ({ ...fd, startUrl: e.target.value }))} placeholder="https://demo.acme.com" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Description</label>
                <input className={inputCls} value={formData.description}
                  onChange={e => setFormData(fd => ({ ...fd, description: e.target.value }))} placeholder="What this demo shows" />
              </div>

              {/* Login (optional) */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-3">
                <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={formData.showLogin}
                    onChange={e => setFormData(fd => ({ ...fd, showLogin: e.target.checked }))} />
                  This demo environment requires login
                </label>
                {formData.showLogin && (
                  <div className="mt-3 space-y-3">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      Login runs <span className="font-medium">before the screen is shared</span> — customers never see the login page or credentials.
                      After the first successful login the browser session is saved and reused, so later demos skip login entirely.
                      Use a demo account only.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Username / email</label>
                        <input className={inputCls} value={formData.login.username} autoComplete="off"
                          onChange={e => setLogin({ username: e.target.value })} placeholder="demo@yourproduct.com" />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Password {formData.login.hasPassword && <span className="text-green-600 dark:text-green-500">(saved ✓ — leave blank to keep)</span>}
                        </label>
                        <input className={inputCls} type="password" value={formData.login.password} autoComplete="new-password"
                          onChange={e => setLogin({ password: e.target.value })}
                          placeholder={formData.login.hasPassword ? '••••••••' : 'demo account password'} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                          Login steps — use <code className="font-mono">{'{{demo.username}}'}</code> and <code className="font-mono">{'{{demo.password}}'}</code> as the typed text; they are filled in securely at run time
                        </label>
                        <button onClick={addLoginAction} className="text-[11px] text-gray-500 dark:text-gray-400 hover:underline flex items-center gap-1">
                          <PlusIcon className="w-3 h-3" /> Add step
                        </button>
                      </div>
                      <div className="space-y-1.5">
                        {formData.login.actions.map((a, ai) => {
                          const spec = ACTION_TYPES.find(t => t.value === a.type) || ACTION_TYPES[0];
                          return (
                            <div key={ai} className="flex items-center gap-1.5">
                              <select className={`${inputCls} !w-40`} value={a.type}
                                onChange={e => setLoginAction(ai, { type: e.target.value, url: '', selector: '', text: '', key: '', y: '', ms: '' })}>
                                {ACTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                              </select>
                              {spec.fields.map((f) => (
                                <input key={f} className={inputCls} value={a[f] ?? ''}
                                  placeholder={f === 'url' ? 'https://…/login' : f === 'selector' ? 'CSS selector (e.g. input[type=email])' : f === 'text' ? '{{demo.username}} or {{demo.password}}' : f}
                                  onChange={e => setLoginAction(ai, { [f]: e.target.value })} />
                              ))}
                              <button onClick={() => removeLoginAction(ai)} className="p-1 text-gray-400 hover:text-red-600"><XMarkIcon className="w-4 h-4" /></button>
                            </div>
                          );
                        })}
                        {formData.login.actions.length === 0 && (
                          <p className="text-[11px] text-gray-400">
                            Example: Type into field <code className="font-mono">input[type=email]</code> → <code className="font-mono">{'{{demo.username}}'}</code>, Click <code className="font-mono">button[type=submit]</code>, Type into field <code className="font-mono">input[type=password]</code> → <code className="font-mono">{'{{demo.password}}'}</code>, Click submit.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Product knowledge — powers free-drive ("show me X") */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Product knowledge — helps the agent show ANY area on request, beyond the scripted steps
                  </label>
                  <button onClick={() => setFormData(fd => ({ ...fd, productMap: [...fd.productMap, emptyArea()] }))}
                    className="text-[11px] text-gray-500 dark:text-gray-400 hover:underline flex items-center gap-1">
                    <PlusIcon className="w-3 h-3" /> Add area
                  </button>
                </div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  List the product's areas (name, URL, one-liner). When a customer asks to see something, the agent jumps straight to the right page — and can offer these areas by name.
                </p>
                <div className="space-y-1.5">
                  {formData.productMap.map((area, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <input className={`${inputCls} !w-44`} value={area.title} placeholder="Area (e.g. Leads)"
                        onChange={e => setFormData(fd => ({ ...fd, productMap: fd.productMap.map((a, j) => j === i ? { ...a, title: e.target.value } : a) }))} />
                      <input className={inputCls} value={area.url} placeholder="https://demo.acme.com/crm/leads"
                        onChange={e => setFormData(fd => ({ ...fd, productMap: fd.productMap.map((a, j) => j === i ? { ...a, url: e.target.value } : a) }))} />
                      <input className={inputCls} value={area.description} placeholder="What it's for (one line)"
                        onChange={e => setFormData(fd => ({ ...fd, productMap: fd.productMap.map((a, j) => j === i ? { ...a, description: e.target.value } : a) }))} />
                      <button onClick={() => setFormData(fd => ({ ...fd, productMap: fd.productMap.filter((_, j) => j !== i) }))}
                        className="p-1 text-gray-400 hover:text-red-600"><XMarkIcon className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {formData.productMap.length === 0 && (
                    <p className="text-[11px] text-gray-400">No areas yet — e.g. Leads, Opportunities, Invoicing, Inventory…</p>
                  )}
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Notes for the agent — demo-account contents, terminology, selling points, and anything it should NOT touch
                  </label>
                  <textarea className={`${inputCls} !h-20 resize-y`} value={formData.productNotes}
                    onChange={e => setFormData(fd => ({ ...fd, productNotes: e.target.value }))}
                    placeholder={'e.g. The demo account has sample data in Sales only. Don’t open Settings. "Branches" = store locations.'} />
                </div>
              </div>

              {/* Beats */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                    Steps — browser actions run exactly as written; the agent narrates the talking points in its own words
                  </label>
                  <button onClick={addBeat} className="text-[11px] font-medium text-gray-700 dark:text-gray-300 hover:underline flex items-center gap-1">
                    <PlusIcon className="w-3 h-3" /> Add step
                  </button>
                </div>
                <div className="space-y-3">
                  {formData.beats.map((beat, bi) => (
                    <div key={bi} className="rounded-xl border border-gray-200 dark:border-gray-800 p-3 bg-gray-50/50 dark:bg-[#1a1a1a]/50">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[11px] font-semibold text-gray-400 w-10">#{bi + 1}</span>
                        <input className={inputCls} value={beat.title || ''} placeholder={`Step title (e.g. "Leads list")`}
                          onChange={e => setBeat(bi, { title: e.target.value })} />
                        <button onClick={() => moveBeat(bi, -1)} disabled={bi === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"><ChevronUpIcon className="w-4 h-4" /></button>
                        <button onClick={() => moveBeat(bi, 1)} disabled={bi === formData.beats.length - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"><ChevronDownIcon className="w-4 h-4" /></button>
                        <button onClick={() => removeBeat(bi)} disabled={formData.beats.length === 1} className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"><TrashIcon className="w-4 h-4" /></button>
                      </div>
                      <textarea
                        className={`${inputCls} min-h-[64px]`}
                        value={beat.say}
                        placeholder="Talking points — what the agent should get across in this step (it will phrase this naturally, tailored to the audience)"
                        onChange={e => setBeat(bi, { say: e.target.value })}
                      />
                      <div className="mt-2 space-y-1.5">
                        {beat.actions.map((a, ai) => {
                          const spec = ACTION_TYPES.find(t => t.value === a.type) || ACTION_TYPES[0];
                          return (
                            <div key={ai} className="flex items-center gap-1.5">
                              <select
                                className={`${inputCls} !w-40`}
                                value={a.type}
                                onChange={e => setAction(bi, ai, { type: e.target.value, url: '', selector: '', text: '', key: '', y: '', ms: '' })}
                              >
                                {ACTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                              </select>
                              {spec.fields.map((f) => (
                                <input
                                  key={f}
                                  className={inputCls}
                                  value={a[f] ?? ''}
                                  placeholder={f === 'url' ? 'https://…' : f === 'selector' ? 'CSS selector (e.g. #login-btn)' : f === 'y' ? 'pixels (negative = up)' : f === 'ms' ? 'milliseconds' : f}
                                  onChange={e => setAction(bi, ai, { [f]: e.target.value })}
                                />
                              ))}
                              <button onClick={() => removeAction(bi, ai)} className="p-1 text-gray-400 hover:text-red-600"><XMarkIcon className="w-4 h-4" /></button>
                            </div>
                          );
                        })}
                        <button onClick={() => addAction(bi)} className="text-[11px] text-gray-500 dark:text-gray-400 hover:underline flex items-center gap-1">
                          <PlusIcon className="w-3 h-3" /> Add browser action
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 dark:border-gray-800">
              <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <input type="checkbox" checked={formData.enabled}
                  onChange={e => setFormData(fd => ({ ...fd, enabled: e.target.checked }))} />
                Enabled (agents can give this demo)
              </label>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowModal(false)} className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-[#222222]">
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!canSave || saving}
                  className="px-4 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving…' : (editing ? 'Save changes' : 'Create demo')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDemos;
